import { callFunction } from "./functions-client";

// Referral program (LOS Phase 14). Default reward: 10% of the referred member's
// qualifying amount, with a 3-month (90-day) attribution cookie.
export const REFERRAL_REWARD_PERCENT = 10;
export const REFERRAL_WINDOW_DAYS = 90;
const REFERRAL_COOKIE = "rt_ref";

export interface ReferralStats {
  signed_up: number;
  converted: number;
  reversed: number;
  reward_total_cents: number;
}

export interface ReferralRow {
  id: string;
  status: "signed_up" | "converted" | "reversed";
  reward_cents: number;
  reward_percent?: number;
  created_at: string;
  referred_name: string | null;
}

export interface ReferralDashboard {
  code: string;
  stats: ReferralStats;
  referrals: ReferralRow[];
  reward_percent?: number;
  attribution_window_days?: number;
}

export const getReferralDashboard = () =>
  callFunction<ReferralDashboard>("referral/dashboard", { method: "GET" });

export const recordReferral = (code: string) =>
  callFunction<{ ok: boolean; status: string }>("referral/record", { body: { code } });

// Credit one monthly Pro payment by a referred member (10%, up to 12 months / $120).
export const creditReferral = (referred_account_id: string, amount_cents: number) =>
  callFunction<{ ok: boolean; credited_cents?: number; reward_cents: number; months_credited?: number }>(
    "referral/credit", { body: { referred_account_id, amount_cents } });

// Build the shareable signup link for a code from the current origin.
export const referralLink = (code: string) =>
  `${typeof window !== "undefined" ? window.location.origin : "https://app.recruittrucker.com"}/register?ref=${encodeURIComponent(code)}`;

// ── 3-month attribution cookie ───────────────────────────────────────────────
// Capture a `?ref=CODE` from any landing URL into a 90-day cookie, so a visitor who
// signs up later within the window is still attributed to the referrer.

/** If the current URL carries `?ref=`, persist it for the attribution window. */
export function captureReferralFromUrl(): void {
  if (typeof window === "undefined") return;
  const ref = new URLSearchParams(window.location.search).get("ref");
  if (ref && /^[A-Za-z0-9]{1,32}$/.test(ref)) setReferralCookie(ref.toUpperCase());
}

/** The stored referral code (URL param wins, else the cookie), or null. */
export function storedReferralCode(): string | null {
  if (typeof window === "undefined") return null;
  const fromUrl = new URLSearchParams(window.location.search).get("ref");
  if (fromUrl && /^[A-Za-z0-9]{1,32}$/.test(fromUrl)) return fromUrl.toUpperCase();
  return readCookie(REFERRAL_COOKIE);
}

export function clearReferralCookie(): void {
  if (typeof document !== "undefined") document.cookie = `${REFERRAL_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
}

function setReferralCookie(code: string): void {
  if (typeof document === "undefined") return;
  const maxAge = REFERRAL_WINDOW_DAYS * 24 * 60 * 60;
  document.cookie = `${REFERRAL_COOKIE}=${encodeURIComponent(code)}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}
